import { useEffect, useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { getErrorMessage } from '@/shared/api/errorMessage';
import { isApiError } from '@/shared/api/errors';
import type { FileRef } from '@/shared/api/types';
import { MAX_UPLOAD_BYTES, uploadFile } from '@/shared/api/upload';
import { useFormatters } from '@/shared/i18n/format';
import { matchesAccept } from '@/shared/lib/files';

import { describeUploadStatus } from './uploadStatus';

export interface UploadTask {
  key: string;
  file: File;
  progress: number;
  status: 'uploading' | 'error';
  error?: string;
  /** What the upload is doing besides sending: retrying after a broken connection, waiting for the network, resumed. */
  note?: string;
  controller: AbortController;
}

export interface UseFileUploadsOptions {
  value: readonly FileRef[];
  onChange: (files: FileRef[]) => void;
  multiple?: boolean;
  /** Allowed types (`image/*,.pdf`), enforced for every way of adding files: picker, drag & drop, paste. */
  accept?: string;
  maxSize?: number;
  maxFiles?: number;
  disabled?: boolean;
  onUploadingChange?: (uploading: boolean) => void;
}

let taskSeq = 0;

/**
 * Headless upload state for the Files API: queue, progress, cancel, retry, validation and drag & drop.
 * `FileUploader` renders it; composers use it directly to place the button and the progress list separately.
 */
export function useFileUploads({
  value,
  onChange,
  multiple = true,
  accept,
  maxSize = MAX_UPLOAD_BYTES,
  maxFiles,
  disabled = false,
  onUploadingChange,
}: UseFileUploadsOptions) {
  const { t } = useTranslation('shared');
  const fmt = useFormatters();
  const [tasks, setTasks] = useState<UploadTask[]>([]);
  const [rejected, setRejected] = useState<string[]>([]);
  const [dragging, setDragging] = useState(false);

  // Uploads complete asynchronously and must append to the newest committed list.
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const tasksRef = useRef(tasks);
  useEffect(() => {
    valueRef.current = value;
    onChangeRef.current = onChange;
    tasksRef.current = tasks;
  });

  const uploading = tasks.some((task) => task.status === 'uploading');
  useEffect(() => onUploadingChange?.(uploading), [uploading, onUploadingChange]);

  // Abort in-flight uploads on unmount.
  useEffect(() => () => tasksRef.current.forEach((task) => task.controller.abort()), []);

  const patchTask = (key: string, patch: Partial<UploadTask>) =>
    setTasks((list) => list.map((task) => (task.key === key ? { ...task, ...patch } : task)));

  const run = (task: UploadTask) => {
    uploadFile(task.file, {
      signal: task.controller.signal,
      onProgress: ({ percent }) => patchTask(task.key, { progress: percent }),
      onStatus: (status) => patchTask(task.key, { note: describeUploadStatus(status, t, fmt.fileSize) }),
    })
      .then((ref) => {
        const next = [...valueRef.current, ref];
        valueRef.current = next;
        onChangeRef.current(next);
        setTasks((list) => list.filter((x) => x.key !== task.key));
      })
      .catch((error: unknown) => {
        if (isApiError(error) && error.isAborted) {
          setTasks((list) => list.filter((x) => x.key !== task.key));
          return;
        }
        patchTask(task.key, { status: 'error', error: getErrorMessage(error) });
      });
  };

  const addFiles = (files: File[]) => {
    if (disabled || files.length === 0) return;
    const room = maxFiles === undefined ? Infinity : maxFiles - valueRef.current.length - tasks.length;
    const accepted: File[] = [];
    const problems: string[] = [];
    for (const file of multiple ? files : files.slice(0, 1)) {
      if (!matchesAccept(file, accept)) problems.push(t('files.notAccepted', { name: file.name }));
      else if (file.size > maxSize)
        problems.push(t('files.tooLarge', { name: file.name, max: fmt.fileSize(maxSize) }));
      else if (accepted.length >= room) problems.push(t('files.tooMany', { count: maxFiles ?? 0 }));
      else accepted.push(file);
    }
    setRejected(problems);
    if (!multiple && accepted.length > 0) {
      tasks.forEach((task) => task.controller.abort());
      if (valueRef.current.length > 0) {
        valueRef.current = [];
        onChangeRef.current([]);
      }
    }
    const created = accepted.map<UploadTask>((file) => ({
      key: `upload-${++taskSeq}`,
      file,
      progress: 0,
      status: 'uploading',
      controller: new AbortController(),
    }));
    setTasks((list) => [...(multiple ? list : []), ...created]);
    created.forEach(run);
  };

  /** Files that are already stored (an attachment source such as "Choose from Disk"): appended like finished uploads. */
  const addFileRefs = (refs: readonly FileRef[]) => {
    if (disabled || refs.length === 0) return;
    const known = new Set(valueRef.current.map((f) => f.id));
    const fresh = refs.filter((ref) => !known.has(ref.id));
    const room =
      maxFiles === undefined ? Infinity : Math.max(0, maxFiles - valueRef.current.length - tasks.length);
    const accepted = multiple ? fresh.slice(0, room) : fresh.slice(0, 1);
    setRejected(accepted.length < fresh.length ? [t('files.tooMany', { count: maxFiles ?? 1 })] : []);
    if (accepted.length === 0) return;
    if (!multiple) tasks.forEach((task) => task.controller.abort());
    const next = multiple ? [...valueRef.current, ...accepted] : accepted;
    valueRef.current = next;
    onChangeRef.current(next);
  };

  const retry = (task: UploadTask) => {
    const fresh: UploadTask = {
      ...task,
      status: 'uploading',
      progress: 0,
      error: undefined,
      note: undefined,
      controller: new AbortController(),
    };
    setTasks((list) => list.map((x) => (x.key === task.key ? fresh : x)));
    run(fresh);
  };

  const cancel = (task: UploadTask) => {
    if (task.status === 'uploading') task.controller.abort();
    else setTasks((list) => list.filter((x) => x.key !== task.key));
  };

  const accepts = (e: DragEvent) => !disabled && e.dataTransfer.types.includes('Files');
  const dragProps = {
    onDragEnter: (e: DragEvent) => {
      if (!accepts(e)) return;
      e.preventDefault();
      setDragging(true);
    },
    onDragOver: (e: DragEvent) => {
      if (!accepts(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    },
    onDragLeave: (e: DragEvent) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false);
    },
    onDrop: (e: DragEvent) => {
      if (!accepts(e)) return;
      e.preventDefault();
      setDragging(false);
      addFiles(Array.from(e.dataTransfer.files));
    },
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files ?? []));
    e.target.value = '';
  };

  return {
    tasks,
    rejected,
    dragging,
    uploading,
    addFiles,
    addFileRefs,
    retry,
    cancel,
    dragProps,
    onInputChange,
    maxSize,
    multiple,
    accept,
    /** How many more files fit (undefined = no limit). */
    remaining: maxFiles === undefined ? undefined : Math.max(0, maxFiles - value.length - tasks.length),
    disabled,
  };
}

export type FileUploadsState = ReturnType<typeof useFileUploads>;
